import { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * A single-choice filter control: every option visible at once, with a honey plate that TRAVELS to
 * the selected one (a thin underline rail until 2026-09-28; `.seg-rail` kept its class name).
 *
 * WHY THIS EXISTS. The filter chrome on every list screen was a `.chip-row` — a horizontally
 * scrolling strip of pills whose only affordance was a mask fade at the edges. On the bag that
 * hid two of five categories at the 390 design viewport; in the wardrobe drawer it hid two of six
 * rarities. A filter whose options you cannot see is a filter you do not use, and a scrolling
 * strip inside a screen that also scrolls vertically is the exact gesture ambiguity §11.2 warns
 * about.
 *
 * WHY IT CAN HOLD THEM ALL. Options are `flex: 1 1 auto` at `--fs-1`, and the tray is allowed to
 * WRAP rather than scroll or truncate. Measured at the two design viewports, both real option
 * sets fit on one line; the wrap is the honest fallback for a narrower phone or a larger system
 * font, and costs one extra row rather than hiding anything.
 *
 * WHY THE RAIL IS MEASURED RATHER THAN STEPPED. The dock's plate (`.nav-plate`) slides by whole
 * columns — `grid-auto-columns: 1fr`, no gap, `translateX(index * 100%)` — with nothing measured
 * in JS. That cannot be reused here, and the reason is worth recording because it looks like it
 * should work: `1fr` is `minmax(AUTO, 1fr)`, and that auto minimum lets a long label force its own
 * column wider. Measured on the six rarity options, the columns come out
 * `52.9 / 65 / 78.1 / 52.9 / 56.2 / 52.9` — so there is no uniform step to translate by, and a
 * stepped plate lands up to 43.8px off (raw-ok: measurements in prose, not declarations).
 * Forcing `minmax(0, 1fr)` does equalise them (all 59.7) and immediately ellipsises "Uncommon" —
 * precisely what this control's `flex: 1 1 auto` exists to avoid. So the rail reads the active option's own box instead. See
 * docs/demos/segmented-control-demo.html, which measures all five approaches.
 *
 * ACCESSIBILITY. Deliberately the same shape the `.chip-filter` rows already used — a
 * `role="group"` of `aria-pressed` buttons, not a `radiogroup`. Two reasons: it keeps every
 * existing test's `getByRole('button', { name })` query working, and a radiogroup implies arrow-
 * key roving focus that this control does not implement. Toggle buttons announce correctly
 * without that contract. The rail itself is `aria-hidden` — selection is already carried by
 * `aria-pressed`, and by colour AND weight visually, so the rail is a third cue rather than the
 * only one (§11.8).
 */
export type SegmentedOption<T extends string> = {
  key: T;
  label: string;
};

export type SegmentedControlProps<T extends string> = {
  /** Names the group for assistive tech, e.g. "Filter by category". */
  label: string;
  options: SegmentedOption<T>[];
  value: T;
  onChange: (key: T) => void;
  /** Offline/read-only screens disable the whole control (native `disabled`, so it is inert even
   *  if a click is dispatched straight at an option — the same rule §11.11 applies elsewhere). */
  disabled?: boolean;
  /** Extra class on the tray, for a screen that needs its own spacing hook. */
  className?: string;
};

export function SegmentedControl<T extends string>({
  label, options, value, onChange, disabled = false, className,
}: SegmentedControlProps<T>) {
  const trayRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement | null>(null);

  /**
   * Writes the active option's own geometry onto the tray as custom properties. Two details are
   * load-bearing:
   *
   * `clientLeft`/`clientTop` are the tray's own border widths. An absolutely-positioned rail is
   * placed against the tray's PADDING box while `getBoundingClientRect` is border-box, so without
   * subtracting them the rail sits a constant border-width off. It measured 1px out (raw-ok:
   * prose, not a declaration) before this was added — small enough to look like a rounding
   * artefact rather than a bug.
   *
   * `y` is written as well as `x` because the tray is allowed to wrap. On a narrow phone or at a
   * large system font the active option can be on the second row, and a rail that only tracks `x`
   * would sit under the wrong row entirely — the one case a stepped/transform-only plate cannot
   * express at all.
   */
  const place = useCallback(() => {
    const tray = trayRef.current;
    const active = activeRef.current;
    if (!tray || !active) return;
    const t = tray.getBoundingClientRect();
    const a = active.getBoundingClientRect();
    tray.style.setProperty('--seg-x', `${a.left - t.left - tray.clientLeft}px`);
    tray.style.setProperty('--seg-y', `${a.top - t.top - tray.clientTop}px`);
    tray.style.setProperty('--seg-w', `${a.width}px`);
    tray.style.setProperty('--seg-h', `${a.height}px`);
  }, []);

  useLayoutEffect(() => {
    place();
    const tray = trayRef.current;
    // Same guard the old preview fit uses: jsdom has no ResizeObserver, and this must not throw there.
    if (!tray || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(place);
    ro.observe(tray);
    return () => ro.disconnect();
  }, [place, value, options]);

  useLayoutEffect(() => {
    // A late webfont swap re-measures every label without resizing the tray, so the
    // ResizeObserver above would never fire for it. Optional-chained: `document.fonts` is absent
    // in jsdom.
    document.fonts?.ready.then(place).catch(() => {});
  }, [place]);

  return (
    <div ref={trayRef} className={className ? `seg ${className}` : 'seg'} role="group" aria-label={label}>
      <span className="seg-rail" aria-hidden="true" />
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          ref={o.key === value ? activeRef : undefined}
          className={o.key === value ? 'seg-option seg-option-active' : 'seg-option'}
          aria-pressed={o.key === value}
          disabled={disabled}
          onClick={() => onChange(o.key)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
